import React, { useRef, useEffect } from 'react';

const NUM_SHEEP = 6;
const MIN_DIST = 60;

let lastSheepBaa  = 0;
let lastRoosterCrow = 0;
let lastChickenCluck = 0;
let soundEnabled = false;

// ─── Factory de animais ───────────────────────────────────────────────────────

function makeSheep(W, H, index) {
  const grassY = H * 0.58;
  const cols = 3, col = index % cols, row = Math.floor(index / cols);
  const cellW = (W - 120) / cols;
  return {
    kind: 'sheep',
    x: 60 + col * cellW + cellW * 0.1 + Math.random() * cellW * 0.8,
    y: grassY + 55 + row * 90 + Math.random() * 40,
    vx: (Math.random() - 0.5) * 0.8,
    jumpOffset: 0, jumpVelocity: 0, isJumping: false,
    jumpTimer: 120 + Math.floor(Math.random() * 280),
    soundAlpha: 0,
    soundTimer: 360 + index * 110 + Math.floor(Math.random() * 400),
    facing: Math.random() > 0.5 ? 1 : -1,
    size: 0.78 + Math.random() * 0.42,
  };
}

function makeRooster(W, H) {
  const grassY = H * 0.58;
  return {
    kind: 'rooster',
    x: W * 0.5 + (Math.random() - 0.5) * 80,
    y: grassY + 70 + Math.random() * 60,
    vx: (Math.random() - 0.5) * 0.5,
    jumpOffset: 0, jumpVelocity: 0, isJumping: false,
    jumpTimer: 200 + Math.floor(Math.random() * 300),
    soundAlpha: 0,
    soundTimer: 480 + Math.floor(Math.random() * 600),
    facing: 1,
    size: 0.9 + Math.random() * 0.2,
  };
}

function makeChicken(W, H, index) {
  const grassY = H * 0.58;
  return {
    kind: 'chicken',
    x: W * (0.25 + index * 0.5) + (Math.random() - 0.5) * 120,
    y: grassY + 60 + Math.random() * 80,
    vx: (Math.random() - 0.5) * 0.6,
    jumpOffset: 0, jumpVelocity: 0, isJumping: false,
    jumpTimer: 150 + Math.floor(Math.random() * 200),
    soundAlpha: 0,
    soundTimer: 300 + index * 180 + Math.floor(Math.random() * 400),
    facing: Math.random() > 0.5 ? 1 : -1,
    size: 0.65 + Math.random() * 0.2,
  };
}

// ─── Áudio ───────────────────────────────────────────────────────────────────

function playSound(acRef, buffersRef, kind) {
  if (!soundEnabled) return;
  // Som apenas dos carneiros — galos e galinhas ficam mudos.
  if (kind !== 'sheep') return;
  const now = Date.now();
  if (kind === 'sheep')   { if (now - lastSheepBaa    < 4500) return; lastSheepBaa    = now; }
  if (kind === 'rooster') { if (now - lastRoosterCrow < 8000) return; lastRoosterCrow = now; }
  if (kind === 'chicken') { if (now - lastChickenCluck < 4000) return; lastChickenCluck = now; }
  try {
    const ac = acRef.current;
    const buf = buffersRef.current[kind];
    if (!ac || !buf || ac.state !== 'running') return;
    const src = ac.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = kind === 'sheep'
      ? 0.88 + Math.random() * 0.24
      : kind === 'chicken'
        ? 0.9 + Math.random() * 0.2
        : 1.0;
    const gain = ac.createGain();
    gain.gain.value = kind === 'rooster' ? 0.8 : 0.7;
    src.connect(gain); gain.connect(ac.destination);
    src.start(ac.currentTime);
  } catch (_) {}
}

// ─── Desenho ─────────────────────────────────────────────────────────────────

function drawCloud(ctx, x, y, sc) {
  ctx.fillStyle = 'rgba(255,255,255,0.9)';
  [[0,0,28],[26,-8,22],[50,2,26],[76,-4,22],[22,10,18],[-8,-2,16]].forEach(([dx,dy,r]) => {
    ctx.beginPath(); ctx.arc(x+dx*sc, y+dy*sc, r*sc, 0, Math.PI*2); ctx.fill();
  });
}

function drawSoundBubble(ctx, text, hx, hy, f, s, alpha) {
  if (alpha <= 0) return;
  ctx.globalAlpha = Math.min(1, alpha);
  const bx = hx + 20 * f, by = hy - 32;
  const tw = Math.max(46, text.length * 7 * s);
  ctx.fillStyle = 'white'; ctx.strokeStyle = '#bbb'; ctx.lineWidth = 1.5;
  ctx.beginPath();
  const [lx, ly, lw, lh, lr] = [bx - tw/2, by - 13, tw, 24, 7];
  ctx.moveTo(lx+lr, ly);
  ctx.lineTo(lx+lw-lr, ly); ctx.arcTo(lx+lw, ly, lx+lw, ly+lr, lr);
  ctx.lineTo(lx+lw, ly+lh-lr); ctx.arcTo(lx+lw, ly+lh, lx+lw-lr, ly+lh, lr);
  ctx.lineTo(lx+lr, ly+lh); ctx.arcTo(lx, ly+lh, lx, ly+lh-lr, lr);
  ctx.lineTo(lx, ly+lr); ctx.arcTo(lx, ly, lx+lr, ly, lr);
  ctx.closePath(); ctx.fill(); ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(bx-8, by+11); ctx.lineTo(bx-14, by+24); ctx.lineTo(bx-2, by+11);
  ctx.fillStyle = 'white'; ctx.fill();
  ctx.strokeStyle = '#bbb'; ctx.lineWidth = 1.5; ctx.stroke();
  ctx.fillStyle = '#444';
  ctx.font = `bold ${11*s}px Arial`;
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.fillText(text, bx, by);
  ctx.globalAlpha = 1;
}

function drawSheep(ctx, sh, t) {
  const { x, y, facing: f, size: s, jumpOffset, isJumping, soundAlpha } = sh;
  ctx.save(); ctx.translate(x, y - jumpOffset);
  const shadowSc = Math.max(0.3, 1 - jumpOffset * 0.012);
  ctx.fillStyle = `rgba(0,0,0,${0.13 * shadowSc})`;
  ctx.beginPath(); ctx.ellipse(0, jumpOffset+5, 22*s*shadowSc, 5*s*shadowSc, 0, 0, Math.PI*2); ctx.fill();
  const moving = Math.abs(sh.vx) > 0.3 || isJumping;
  const swing = moving ? Math.sin(t*0.2)*5*s : 0;
  ctx.strokeStyle = '#3d2b1a'; ctx.lineWidth = 3.5*s; ctx.lineCap = 'round';
  [-11,-4,4,11].forEach(bx => {
    ctx.beginPath(); ctx.moveTo(bx*s,0); ctx.lineTo(bx*s+(bx<0?swing:-swing),18*s); ctx.stroke();
    ctx.fillStyle='#2a1a0a'; ctx.beginPath(); ctx.ellipse(bx*s+(bx<0?swing:-swing),19*s,2.5*s,2*s,0,0,Math.PI*2); ctx.fill();
  });
  [[0,-4,18],[-13,-2,13],[13,-2,13],[-8,-13,12],[8,-13,12],[0,-18,11],[-14,-10,9],[14,-10,9],[0,-26,8]].forEach(([dx,dy,r]) => {
    ctx.fillStyle='#f2efe9'; ctx.strokeStyle='#d9d5cf'; ctx.lineWidth=0.6;
    ctx.beginPath(); ctx.arc(dx*s,dy*s,r*s,0,Math.PI*2); ctx.fill(); ctx.stroke();
  });
  const hx=24*s*f, hy=-16*s;
  ctx.fillStyle='#ebe8e2'; ctx.beginPath(); ctx.arc(hx,hy,11*s,0,Math.PI*2); ctx.fill();
  ctx.strokeStyle='#ccc9c3'; ctx.lineWidth=0.6; ctx.stroke();
  ctx.fillStyle='#d4b090'; ctx.beginPath(); ctx.ellipse(hx-6*s*f,hy-9*s,3.5*s,6*s,f*0.45,0,Math.PI*2); ctx.fill();
  ctx.fillStyle='#1a1a1a'; ctx.beginPath(); ctx.arc(hx+4*s*f,hy-2*s,2.2*s,0,Math.PI*2); ctx.fill();
  ctx.fillStyle='white'; ctx.beginPath(); ctx.arc(hx+5.2*s*f,hy-3.2*s,0.8*s,0,Math.PI*2); ctx.fill();
  ctx.fillStyle='#cc9988'; ctx.beginPath(); ctx.ellipse(hx+7.5*s*f,hy+1.5*s,3*s,2*s,0,0,Math.PI*2); ctx.fill();
  ctx.strokeStyle='#999'; ctx.lineWidth=1.2*s; ctx.beginPath(); ctx.arc(hx+2*s*f,hy+4*s,4*s,0.15,Math.PI-0.15); ctx.stroke();
  drawSoundBubble(ctx, 'BAA!', hx, hy, f, s, soundAlpha);
  ctx.restore();
}

function drawRooster(ctx, an, t) {
  const { x, y, facing: f, size: s, jumpOffset, isJumping, soundAlpha } = an;
  ctx.save(); ctx.translate(x, y - jumpOffset);

  // Sombra
  const shSc = Math.max(0.3, 1 - jumpOffset * 0.012);
  ctx.fillStyle = `rgba(0,0,0,${0.13*shSc})`;
  ctx.beginPath(); ctx.ellipse(0, jumpOffset+5, 20*s*shSc, 4*s*shSc, 0, 0, Math.PI*2); ctx.fill();

  // Pernas
  const moving = Math.abs(an.vx) > 0.3 || isJumping;
  const legSwing = moving ? Math.sin(t*0.22)*4*s : 0;
  ctx.strokeStyle = '#c8821a'; ctx.lineWidth = 2.8*s; ctx.lineCap = 'round';
  [[-5,5],[5,-5]].forEach(([lx, sw], i) => {
    const fx = lx*s, tx = (lx + (i===0?-legSwing:legSwing));
    ctx.beginPath(); ctx.moveTo(fx, 0); ctx.lineTo(tx*s, 16*s); ctx.stroke();
    // 3 dedos
    ctx.lineWidth = 1.5*s;
    [[-8,4],[-1,6],[7,3]].forEach(([dx,dy]) => {
      ctx.beginPath(); ctx.moveTo(tx*s,16*s); ctx.lineTo((tx+dx)*s,(16+dy)*s); ctx.stroke();
    });
    ctx.lineWidth = 2.8*s;
  });

  // Penas da cauda (coloridas)
  const tailBase = [-16*s*f, -8*s];
  const tailAngles = f > 0 ? [2.2,2.5,2.8,3.1] : [0.0,0.3,0.6,0.9];
  const tailColors = ['#1565c0','#2e7d32','#6a1b9a','#e65100'];
  tailColors.forEach((col, i) => {
    const a = tailAngles[i] + Math.sin(t*0.04+i)*0.06;
    const len = (22+i*4)*s;
    ctx.strokeStyle = col; ctx.lineWidth = (4-i*0.5)*s; ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(tailBase[0], tailBase[1]);
    ctx.lineTo(tailBase[0]+Math.cos(a)*len, tailBase[1]+Math.sin(a)*len);
    ctx.stroke();
  });

  // Corpo
  ctx.fillStyle = '#bf360c';
  ctx.beginPath(); ctx.ellipse(0,-8*s,18*s,13*s,0,0,Math.PI*2); ctx.fill();
  // Asa
  ctx.fillStyle = '#e64a19';
  ctx.beginPath(); ctx.ellipse(-1*s*f,-9*s,13*s,9*s,f*0.25,0,Math.PI*2); ctx.fill();
  // Peito
  ctx.fillStyle = '#ff7043';
  ctx.beginPath(); ctx.ellipse(4*s*f,-6*s,8*s,7*s,0,0,Math.PI*2); ctx.fill();

  // Cabeça
  const hx=18*s*f, hy=-23*s;
  ctx.fillStyle='#bf360c'; ctx.beginPath(); ctx.arc(hx,hy,9*s,0,Math.PI*2); ctx.fill();

  // Crista (comb) — 3 bolhas vermelhas no topo
  ctx.fillStyle='#c62828';
  [[-5,0],[0,-4],[5,0]].forEach(([dx,dy]) => {
    ctx.beginPath(); ctx.arc(hx+dx*s*f, hy-9*s+dy*s, 4.5*s, 0, Math.PI*2); ctx.fill();
  });

  // Barbela (wattle)
  ctx.fillStyle='#c62828';
  ctx.beginPath(); ctx.ellipse(hx+4*s*f, hy+6*s, 3.5*s, 6*s, 0, 0, Math.PI*2); ctx.fill();

  // Bico
  ctx.fillStyle='#f9a825';
  ctx.beginPath();
  ctx.moveTo(hx+5*s*f, hy-2*s);
  ctx.lineTo(hx+15*s*f, hy+1*s);
  ctx.lineTo(hx+5*s*f, hy+4*s);
  ctx.closePath(); ctx.fill();

  // Olho
  ctx.fillStyle='#ffd54f'; ctx.beginPath(); ctx.arc(hx+2*s*f, hy-3*s, 3*s, 0, Math.PI*2); ctx.fill();
  ctx.fillStyle='#1a1a1a'; ctx.beginPath(); ctx.arc(hx+2*s*f, hy-3*s, 1.8*s, 0, Math.PI*2); ctx.fill();
  ctx.fillStyle='white'; ctx.beginPath(); ctx.arc(hx+3*s*f, hy-4*s, 0.7*s, 0, Math.PI*2); ctx.fill();

  drawSoundBubble(ctx, 'COCORICÓ!', hx, hy, f, s, soundAlpha);
  ctx.restore();
}

function drawChicken(ctx, an, t) {
  const { x, y, facing: f, size: s, jumpOffset, isJumping, soundAlpha } = an;
  ctx.save(); ctx.translate(x, y - jumpOffset);

  // Sombra
  const shSc = Math.max(0.3, 1 - jumpOffset * 0.012);
  ctx.fillStyle = `rgba(0,0,0,${0.12*shSc})`;
  ctx.beginPath(); ctx.ellipse(0, jumpOffset+4, 16*s*shSc, 4*s*shSc, 0, 0, Math.PI*2); ctx.fill();

  // Pernas (galinha coça mais rápido)
  const moving = Math.abs(an.vx) > 0.2 || isJumping;
  const legSwing = moving ? Math.sin(t*0.28)*4*s : 0;
  ctx.strokeStyle = '#e0a030'; ctx.lineWidth = 2.2*s; ctx.lineCap = 'round';
  [[-4,4],[4,-4]].forEach(([lx, sw], i) => {
    const fx = lx*s, tx = lx + (i===0?-legSwing:legSwing);
    ctx.beginPath(); ctx.moveTo(fx, 0); ctx.lineTo(tx*s, 14*s); ctx.stroke();
    ctx.lineWidth = 1.3*s;
    [[-6,3],[0,5],[6,2]].forEach(([dx,dy]) => {
      ctx.beginPath(); ctx.moveTo(tx*s,14*s); ctx.lineTo((tx+dx)*s,(14+dy)*s); ctx.stroke();
    });
    ctx.lineWidth = 2.2*s;
  });

  // Cauda curta
  const tailBase = [-12*s*f, -7*s];
  ['#795548','#a1887f','#6d4c41'].forEach((col, i) => {
    const a = (f>0?2.4:0.5) + (i-1)*0.25 + Math.sin(t*0.05+i)*0.05;
    const len = (10+i*3)*s;
    ctx.strokeStyle = col; ctx.lineWidth = 3*s; ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(tailBase[0], tailBase[1]);
    ctx.lineTo(tailBase[0]+Math.cos(a)*len, tailBase[1]+Math.sin(a)*len);
    ctx.stroke();
  });

  // Corpo (mais redondo que galo)
  ctx.fillStyle='#8d6e63';
  ctx.beginPath(); ctx.ellipse(0,-7*s,15*s,11*s,0,0,Math.PI*2); ctx.fill();
  // Peito mais claro
  ctx.fillStyle='#bcaaa4';
  ctx.beginPath(); ctx.ellipse(3*s*f,-5*s,9*s,7*s,0,0,Math.PI*2); ctx.fill();

  // Cabeça
  const hx=15*s*f, hy=-20*s;
  ctx.fillStyle='#8d6e63'; ctx.beginPath(); ctx.arc(hx,hy,8*s,0,Math.PI*2); ctx.fill();

  // Crista pequena
  ctx.fillStyle='#e53935';
  [[-3,0],[1,-3],[5,0]].forEach(([dx,dy]) => {
    ctx.beginPath(); ctx.arc(hx+dx*s*f, hy-8*s+dy*s, 3*s, 0, Math.PI*2); ctx.fill();
  });

  // Barbela
  ctx.fillStyle='#e53935';
  ctx.beginPath(); ctx.ellipse(hx+3*s*f, hy+5*s, 2.5*s, 4*s, 0, 0, Math.PI*2); ctx.fill();

  // Bico
  ctx.fillStyle='#f9a825';
  ctx.beginPath();
  ctx.moveTo(hx+4*s*f, hy-1*s);
  ctx.lineTo(hx+12*s*f, hy+1*s);
  ctx.lineTo(hx+4*s*f, hy+3*s);
  ctx.closePath(); ctx.fill();

  // Olho
  ctx.fillStyle='#ffd54f'; ctx.beginPath(); ctx.arc(hx+2*s*f,hy-2*s,2.5*s,0,Math.PI*2); ctx.fill();
  ctx.fillStyle='#1a1a1a'; ctx.beginPath(); ctx.arc(hx+2*s*f,hy-2*s,1.5*s,0,Math.PI*2); ctx.fill();
  ctx.fillStyle='white'; ctx.beginPath(); ctx.arc(hx+3*s*f,hy-3*s,0.6*s,0,Math.PI*2); ctx.fill();

  drawSoundBubble(ctx, 'CLOC CLOC!', hx, hy, f, s, soundAlpha);
  ctx.restore();
}

// ─── Update de qualquer animal ────────────────────────────────────────────────

function updateAnimal(an, mouse, W, H, acRef, buffersRef) {
  const grassY = H * 0.58;

  // Fuga do cursor / toque
  const dx = an.x - mouse.x;
  const dy = (an.y - an.jumpOffset) - mouse.y;
  const dist = Math.hypot(dx, dy);
  if (dist < 170 && dist > 0) {
    an.vx += (dx / dist) * ((170 - dist) / 170) * 3.5;
  }

  // Passeio aleatório
  an.vx += (Math.random() - 0.5) * 0.12;
  an.vx *= 0.93;
  const spd = Math.abs(an.vx);
  if (spd > 5) an.vx = (an.vx / spd) * 5;
  if (Math.abs(an.vx) > 0.15) an.facing = an.vx > 0 ? 1 : -1;

  an.x = Math.max(40, Math.min(W - 40, an.x + an.vx));
  if (an.x <= 40)    an.vx = Math.abs(an.vx);
  if (an.x >= W - 40) an.vx = -Math.abs(an.vx);

  // Salto
  an.jumpTimer--;
  if (an.jumpTimer <= 0 && !an.isJumping) {
    an.isJumping = true;
    an.jumpVelocity = an.kind === 'rooster' ? 3 + Math.random() * 3 : 4 + Math.random() * 4;
    an.jumpTimer = (an.kind === 'rooster' ? 200 : 130) + Math.floor(Math.random() * 250);
  }
  if (an.isJumping) {
    an.jumpOffset += an.jumpVelocity;
    an.jumpVelocity -= 0.42;
    if (an.jumpOffset <= 0) { an.jumpOffset = 0; an.isJumping = false; }
  }

  // Som
  an.soundTimer--;
  if (an.soundTimer <= 0) {
    an.soundAlpha = 1.2;
    an.soundTimer = (an.kind === 'rooster' ? 600 : 320) + Math.floor(Math.random() * 600);
    playSound(acRef, buffersRef, an.kind);
  }
  if (an.soundAlpha > 0) an.soundAlpha -= 0.022;
}

// ─── Componente principal ─────────────────────────────────────────────────────

export default function SheepBackground() {
  const canvasRef  = useRef(null);
  const stateRef   = useRef({ animals: [], clouds: [], time: 0, mouse: { x: -999, y: -999 } });
  const audioRef   = useRef(null);
  const buffersRef = useRef({});
  const rafRef     = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const state = stateRef.current;

    const init = () => {
      canvas.width  = window.innerWidth;
      canvas.height = window.innerHeight;
      const W = canvas.width, H = canvas.height;
      state.animals = [
        ...Array.from({ length: NUM_SHEEP },  (_, i) => makeSheep(W, H, i)),
        makeRooster(W, H),
        makeChicken(W, H, 0),
        makeChicken(W, H, 1),
      ];
      state.clouds = Array.from({ length: 6 }, (_, i) => ({
        x: (i / 6) * W + Math.random() * 160,
        y: 25 + Math.random() * (H * 0.28),
        sc: 0.5 + Math.random() * 0.75,
        spd: 0.12 + Math.random() * 0.22,
      }));
    };

    init();
    window.addEventListener('resize', init);

    // Carrega os três sons em paralelo
    const loadSounds = async () => {
      try {
        const ac = new (window.AudioContext || window.webkitAudioContext)();
        audioRef.current = ac;
        const files = { sheep: '/sheep-baa.mp3', rooster: '/rooster-crow.mp3', chicken: '/chicken-cluck.mp3' };
        await Promise.all(Object.entries(files).map(async ([key, url]) => {
          const resp = await fetch(url);
          const buf  = await resp.arrayBuffer();
          buffersRef.current[key] = await ac.decodeAudioData(buf);
        }));
      } catch (_) {}
    };
    loadSounds();

    const enableSound = () => {
      const ac = audioRef.current;
      if (!ac) return;
      (ac.state === 'suspended' ? ac.resume() : Promise.resolve()).then(() => { soundEnabled = true; });
    };

    const onMouseMove = (e) => { state.mouse = { x: e.clientX, y: e.clientY }; };
    const onTouchStart = (e) => {
      enableSound();
      const t = e.touches[0]; if (t) state.mouse = { x: t.clientX, y: t.clientY };
    };
    const onTouchMove  = (e) => { const t = e.touches[0]; if (t) state.mouse = { x: t.clientX, y: t.clientY }; };
    const onTouchEnd   = ()  => { state.mouse = { x: -999, y: -999 }; };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('click',    enableSound);
    window.addEventListener('keydown',  enableSound);
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove',  onTouchMove,  { passive: true });
    window.addEventListener('touchend',   onTouchEnd);

    const loop = () => {
      state.time++;
      const t = state.time;
      const W = canvas.width, H = canvas.height;
      const grassY = H * 0.58;

      // Céu
      const sky = ctx.createLinearGradient(0, 0, 0, grassY + 80);
      sky.addColorStop(0, '#0d47a1'); sky.addColorStop(0.45, '#1976d2');
      sky.addColorStop(0.75, '#42a5f5'); sky.addColorStop(1, '#90caf9');
      ctx.fillStyle = sky; ctx.fillRect(0, 0, W, H);

      // Sol
      const sx = W * 0.82, sy = H * 0.1;
      const sunG = ctx.createRadialGradient(sx,sy,0,sx,sy,90);
      sunG.addColorStop(0,'rgba(255,253,210,1)'); sunG.addColorStop(0.3,'rgba(255,230,50,0.85)');
      sunG.addColorStop(0.55,'rgba(255,200,0,0.3)'); sunG.addColorStop(1,'rgba(255,200,0,0)');
      ctx.fillStyle=sunG; ctx.beginPath(); ctx.arc(sx,sy,90,0,Math.PI*2); ctx.fill();
      for (let i = 0; i < 12; i++) {
        const a = (i/12)*Math.PI*2 + t*0.004;
        const len = 28 + Math.sin(t*0.025+i*1.3)*12;
        ctx.strokeStyle=`rgba(255,220,50,${0.12+Math.sin(t*0.03+i)*0.05})`; ctx.lineWidth=2.5;
        ctx.beginPath();
        ctx.moveTo(sx+Math.cos(a)*28,sy+Math.sin(a)*28);
        ctx.lineTo(sx+Math.cos(a)*(55+len),sy+Math.sin(a)*(55+len));
        ctx.stroke();
      }

      // Nuvens
      state.clouds.forEach(c => { c.x=(c.x+c.spd)%(W+250); drawCloud(ctx,c.x-100,c.y,c.sc); });

      // Colinas
      ctx.fillStyle='#66bb6a'; ctx.beginPath(); ctx.moveTo(0,H);
      [0,0.12,0.25,0.38,0.5,0.62,0.75,0.87,1].forEach((px,i,arr) => {
        const hArr=[30,-50,10,-70,20,-45,15,-55,25];
        if (i===0){ctx.lineTo(0,grassY+hArr[i]);return;}
        const ppx=arr[i-1];
        ctx.bezierCurveTo((ppx+(px-ppx)*0.4)*W,grassY+hArr[i-1],(ppx+(px-ppx)*0.6)*W,grassY+hArr[i],px*W,grassY+hArr[i]);
      });
      ctx.lineTo(W,H); ctx.closePath(); ctx.fill();

      // Grama
      const gGrad = ctx.createLinearGradient(0,grassY,0,H);
      gGrad.addColorStop(0,'#4caf50'); gGrad.addColorStop(0.2,'#43a047');
      gGrad.addColorStop(0.6,'#2e7d32'); gGrad.addColorStop(1,'#1b5e20');
      ctx.fillStyle=gGrad; ctx.fillRect(0,grassY,W,H-grassY);
      for (let bx=8;bx<W;bx+=19) {
        const wave=Math.sin(bx*0.07+t*0.022)*3.5, bh=7+Math.sin(bx*0.33)*3.5;
        ctx.strokeStyle=`rgba(129,199,132,${0.35+Math.sin(bx*0.19)*0.12})`; ctx.lineWidth=1.5;
        ctx.beginPath(); ctx.moveTo(bx,grassY);
        ctx.quadraticCurveTo(bx+wave,grassY-bh*0.6,bx+wave*0.6,grassY-bh); ctx.stroke();
      }

      // Cerca
      const fy=grassY+6;
      ctx.fillStyle='#8d6347'; ctx.fillRect(0,fy,W,5);
      ctx.fillStyle='#7a5538'; ctx.fillRect(0,fy+18,W,4);
      for (let fx=25;fx<W;fx+=68){
        ctx.fillStyle='#6d4c35'; ctx.beginPath();
        ctx.moveTo(fx,fy-10); ctx.lineTo(fx+7,fy+28); ctx.lineTo(fx-7,fy+28);
        ctx.closePath(); ctx.fill();
      }

      // Separação entre animais
      const { animals, mouse } = state;
      for (let i=0;i<animals.length;i++) {
        for (let j=i+1;j<animals.length;j++) {
          const a=animals[i], b=animals[j];
          const dx=a.x-b.x, dy=a.y-b.y;
          const dist=Math.hypot(dx,dy);
          if (dist<MIN_DIST && dist>0) {
            const push=((MIN_DIST-dist)/MIN_DIST)*1.3;
            a.vx+=(dx/dist)*push; b.vx-=(dx/dist)*push;
          }
        }
      }

      // Ordena por Y para efeito de profundidade (animais mais abaixo na tela = na frente)
      const sorted = [...animals].sort((a, b) => a.y - b.y);

      sorted.forEach(an => {
        updateAnimal(an, mouse, W, H, audioRef, buffersRef);
        if (an.kind === 'sheep')   drawSheep(ctx, an, t);
        if (an.kind === 'rooster') drawRooster(ctx, an, t);
        if (an.kind === 'chicken') drawChicken(ctx, an, t);
      });

      rafRef.current = requestAnimationFrame(loop);
    };

    loop();

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', init);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('click',    enableSound);
      window.removeEventListener('keydown',  enableSound);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove',  onTouchMove);
      window.removeEventListener('touchend',   onTouchEnd);
      soundEnabled = false;
    };
  }, []);

  return (
    <canvas ref={canvasRef} style={{
      position:'fixed', top:0, left:0, width:'100%', height:'100%', zIndex:0, pointerEvents:'none',
    }} />
  );
}
